import { useAccount } from '@shared/api';
import { FoodActivityRecord } from '@shared/api/types';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import Unit from '@shared/components/content/unit';
import { formatCost, getCurrencySymbol } from '@shared/lib/format';
import { ColumnDef } from '@tanstack/react-table';
import { usePurchasedFoodAndDrinkItem } from '../../purchased-food-and-drink-item-context';

export function usePurchasedFoodAndDrinkItemPurchasesTableColumns(): ColumnDef<FoodActivityRecord>[] {
  const { item } = usePurchasedFoodAndDrinkItem();
  const hasPackConfiguration = !!item.itemUnit;
  const { data: account } = useAccount(true);

  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'period',
      accessorKey: 'timePeriod',
      header: ({ column }) => <HeaderCell column={column} name="Period" />,
      cell: ({ getValue }) => {
        const period = getValue<FoodActivityRecord['timePeriod']>();
        return <TextCell text={period?.name} />;
      },
      meta: {
        label: 'Time period',
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
      enableColumnFilter: false,
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: false,
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<FoodActivityRecord['site']>();

        return <TextCell text={site?.name} />;
      },
    },
    {
      id: 'supplier',
      accessorKey: 'supplier',
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Supplier" />,
      cell: ({ getValue }) => {
        const supplier = getValue<FoodActivityRecord['supplier']>();
        return <TextCell text={supplier?.name} />;
      },
    },
    ...(hasPackConfiguration
      ? ([
          {
            id: 'quantity',
            accessorKey: 'quantity',
            header: ({ column }) => (
              <HeaderCell column={column} name="Packs purchased" numberColumn />
            ),
            cell: ({ getValue, row }) => {
              const recordUnit = row.original.unit?.symbol;

              const quantity = getValue<number>();
              return recordUnit === 'ea' ? (
                <NumberCell number={quantity} />
              ) : (
                <p className="text-right text-muted-foreground">-</p>
              );
            },
          },
        ] as ColumnDef<FoodActivityRecord>[])
      : []),

    {
      id: 'totalMassOrVolume',
      accessorKey: 'totalMassOrVolume',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total weight" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalWeight = getValue<number>();
        return <NumberCell number={totalWeight} />;
      },
    },
    {
      id: 'unit',
      accessorKey: 'unit',
      enableSorting: false,

      header: ({ column }) => <HeaderCell column={column} name="Unit" />,
      cell: ({ getValue }) => {
        const recordUnit = getValue<FoodActivityRecord['unit']>().symbol;
        const itemUnit = item.itemUnit?.symbol || '';
        return <TextCell text={recordUnit === 'ea' ? itemUnit : recordUnit} />;
      },
      enableColumnFilter: false,
    },
    {
      id: 'totalSpend',
      accessorKey: 'totalSpend',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total spend" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalSpend = getValue<number | null | undefined>();
        return (
          <TextCell
            text={formatCost(totalSpend, account?.defaultCurrency)}
            type="number"
          />
        );
      },
    },
    {
      // Actual price paid per kg for this purchase (backend `price_per_kg`,
      // spend ÷ kg-normalized quantity), distinct from the blended list
      // "Average price". accessorFn (with an explicit id) makes the column
      // sortable while keeping the `price` id that maps to the backend sort.
      id: 'price',
      accessorFn: (row) => row.pricePerKg,
      enableSorting: true,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Price"
          numberColumn
          unit={
            <Unit
              variant={`${getCurrencySymbol(account?.defaultCurrency)}/kg`}
            />
          }
        />
      ),
      cell: ({ row }) => (
        <TextCell
          text={formatCost(row.original.pricePerKg, account?.defaultCurrency)}
          type="number"
        />
      ),
    },
  ];
}
